import { gql } from "@apollo/client";
import { SearchSelect } from "./SearchSelect";
import cx from 'classnames';
import { SupplierVariation_SearchSelect_SearchSupplierVariationsQuery, SupplierVariation_SearchSelect_SearchSupplierVariationsQueryVariables } from "../../../../codegen/graphql";
import { DeliveryDestination } from "../../../../common/deliveryDestinations";
import { useCallback } from "react";
import { getAliexpressVariationQuickData } from "../../../../common/aliexpress/getAliexpressQuickData";
import { FaStore } from "react-icons/fa";
import { ConvertedAmount } from "../../ConvertedAmount";
import { formatCurrency } from "../../../utils/currency";
import dayjs from "dayjs";
import client from "../../../graphqlClient";
import { getTranslatedValue } from "../../../../common/getTranslatedValue";

const SEARCH_SUPPLIER_VARIATIONS = gql`
  query SupplierVariation_SearchSelect_SearchSupplierVariations($linkedToAdVariationId: ID) {
    supplierProductVariations(linkedToAdVariationId: $linkedToAdVariationId) {
      sku
      supplierProductId
      data
      supplierName
      lastSyncAt
      quantityOrdered
      supplierProduct {
        id
        note
        name
        supplierName
        data
        externalProductId
      }
      calculatedCost {
        destinationCountry
        totalCostIncludingVAT
        totalCostExcludingVAT
        totalCostVAT
        VATRate
        currency
      }
      availableStock {
        destinationCountry
        availableStock
      }
      typeValues {
        id
        type {
          id
          type
        }
        value
      }
    }
  }
`;

export type SupplierVariationSearchSelect_SupplierVariationOption = SupplierVariation_SearchSelect_SearchSupplierVariationsQuery['supplierProductVariations'][number];

export default function SupplierVariation_SearchSelect({
  selectedSupplierVariation,
  onSupplierVariationChange,
  linkedToAdVariationId,
  deliveryDestination,
}: {
  selectedSupplierVariation: SupplierVariationSearchSelect_SupplierVariationOption | null;
  onSupplierVariationChange: (supplierVariation: SupplierVariationSearchSelect_SupplierVariationOption | null) => void;
  deliveryDestination: DeliveryDestination;
  linkedToAdVariationId?: string;
  lastUsedSupplierProductVariationSku?: string;
}) {
  // Load ads for search
  const loadSupplierVariations = useCallback(async (searchText: string) => {
    if (
      searchText.trim().length < 3
      && !linkedToAdVariationId
    ) {
      return [];
    }
    
    const result = await client.query<
      SupplierVariation_SearchSelect_SearchSupplierVariationsQuery,
      SupplierVariation_SearchSelect_SearchSupplierVariationsQueryVariables
    >({
      query: SEARCH_SUPPLIER_VARIATIONS,
      variables: {
        linkedToAdVariationId: linkedToAdVariationId || null
      },
      fetchPolicy: 'network-only'
    });
    return result.data?.supplierProductVariations || [];
  }, [linkedToAdVariationId]);

  return (
    <SearchSelect<SupplierVariationSearchSelect_SupplierVariationOption>
      withSearch={false}
      modalWidth="large"
      loadOptions={loadSupplierVariations}
      renderOption={(supplierVariation, selectCallback, isSelected) => (
        <SupplierProductVariationOption
          deliveryDestination={deliveryDestination}
          supplierProductVariation={supplierVariation}
          isRenderSelected={false}
          isSelectedOption={isSelected}
          selectCallback={selectCallback}
        />
      )}
      renderSelected={(supplierVariation) => (
        <SupplierProductVariationOption
          deliveryDestination={deliveryDestination}
          supplierProductVariation={supplierVariation}
          isRenderSelected={true}
          isSelectedOption={true}
        />
      )}
      value={selectedSupplierVariation || null}
      onSelect={onSupplierVariationChange}
    />
  )
}



function SupplierProductVariationOption({
  supplierProductVariation,
  deliveryDestination,
  isSelectedOption = false,
  isRenderSelected = false,
  selectCallback,
}: {
  supplierProductVariation: SupplierVariationSearchSelect_SupplierVariationOption,
  deliveryDestination: DeliveryDestination,
  isSelectedOption: boolean,
  isRenderSelected: boolean,
  selectCallback?: () => void
}) {
  const supplierProductData = supplierProductVariation.supplierProduct.data?.[deliveryDestination]?.data;
  const supplierVariationData = supplierProductVariation.data?.[deliveryDestination];
  const storeRatingShippingSpeed = parseFloat(
    supplierProductData?.ae_store_info?.shipping_speed_rating
  )
  const storeRatingCommunication = parseFloat(supplierProductData?.ae_store_info?.communication_rating)
  const storeRatingItemAsDescribed = parseFloat(supplierProductData?.ae_store_info?.item_as_described_rating)
  const storeAverageRating = (storeRatingShippingSpeed + storeRatingCommunication + storeRatingItemAsDescribed) / 3
  const storeName = supplierProductData?.ae_store_info?.store_name

  const cost = supplierProductVariation.calculatedCost.find((cost) => (
    cost.destinationCountry === deliveryDestination
  ));
  const availableStock = supplierProductVariation.availableStock.find((availableStock) => (
    availableStock.destinationCountry === deliveryDestination
  ))?.availableStock ?? -1;
  const isAvailable = availableStock > 0 && (cost?.totalCostIncludingVAT || 0) > 0;

  const aliexpressQuickData = supplierProductVariation.supplierName === 'aliexpress' && supplierVariationData
    ? getAliexpressVariationQuickData(supplierVariationData.variation)
    : null;

  return (
    <div
      className={cx(
        'flex-1',
        !isRenderSelected && 'px-4 py-2',
        !isAvailable && 'opacity-50',
      )}
    >
      <div
        className={cx(
          'flex-1 border-1 border-gray-300 rounded-md p-2 flex justify-between',
          isRenderSelected && 'border-none p-0',
          !isRenderSelected && isAvailable && 'hover:bg-blue-50 hover:border-blue-500',
          !isRenderSelected && isAvailable && 'cursor-pointer',
          isSelectedOption && !isRenderSelected && 'bg-blue-50 border-blue-500',
        )}
        onClick={isAvailable && selectCallback || undefined}
      >
        <div className="px-4 py-4">
          {aliexpressQuickData?.skuImages ? (
            <img 
              src={aliexpressQuickData.skuImages[0]}
              alt={aliexpressQuickData.skuId} 
              referrerPolicy='no-referrer'
              className="h-25 w-25 rounded-md object-cover"
            />
          ) : (
            <div className="h-25 w-25 rounded-md bg-gray-200 flex items-center justify-center text-gray-500">
              No image
            </div>
          )}
        </div>
        <div className="flex-1">
          {supplierProductVariation.supplierProduct?.note && (
            <p className="mb-2 p-1 bg-yellow-50 border border-yellow-200 rounded-lg">
              {supplierProductVariation.supplierProduct?.note}
            </p>
          )}
          <p className="text-gray-500">
            {/* {adVariation.lastUsedSupplierProductVariation?.supplierProductId === supplierProductVariation.supplierProductId
            && adVariation.lastUsedSupplierProductVariation?.sku === supplierProductVariation.sku && (
              <span className="border-1 font-bold border-yellow-500 uppercase mr-2 text-yellow-800 rounded-md px-1 text-sm inline-block mb-2">
                Last used
              </span>
            )}

            {lowestPriceSupplierVariation.cost === supplierProductVariation.cost && (
              <span className="border-1 font-bold border-yellow-500 uppercase mr-2 text-yellow-800 rounded-md px-1 text-sm inline-block mb-2">
                Lowest price
              </span>
            )} */}
          </p>
          <p className="text-lg text-gray-500 flex flex-row items-center gap-2">
            <FaStore className="text-yellow-600" size={20} />
            <span className="text-red-600 font-bold">{storeName}</span>
            <span className="text-yellow-600 text-base font-bold border border-yellow-600 rounded-md px-1 py-0.5 line-height-1 ml-2">
              {storeAverageRating.toFixed(1)}
            </span>
          </p>
          <p className="text-lg text-gray-900">
            {supplierProductVariation.supplierProduct.supplierName} - ID : {supplierProductVariation.supplierProduct.externalProductId} - Sku :{supplierProductVariation.sku}
          </p>
          <p className="text-lg mb-2">
            {getTranslatedValue(supplierProductVariation.supplierProduct?.name, deliveryDestination) || 'Unknown'}
          </p>
          <p className="text-lg font-medium mb-2">
            {supplierProductVariation.typeValues.map((tv: any) => (
              `${getTranslatedValue(tv.type.type, deliveryDestination)}: ${getTranslatedValue(tv.value, deliveryDestination)}`
            )).join(', ')}
          </p>
          
        </div>
        <div className="flex-none ml-4">
          {isAvailable ? (
            <>
              {supplierProductVariation.supplierName === 'aliexpress' ? (
                <>
                  <p
                    className="text-right text-md text-gray-500"
                    title={dayjs(supplierProductVariation[deliveryDestination as keyof typeof supplierProductVariation.data]?.lastSyncAt).format('DD/MM/YYYY à HH:mm')}
                  >
                    Last synced: {dayjs(supplierProductVariation[deliveryDestination]?.lastSyncAt).fromNow()}
                  </p>
                  <p className="text-right text-md text-gray-500 mb-2">
                    Delivery: {supplierVariationData?.freight?.delivery_options?.delivery_option_d_t_o?.[0]?.delivery_date_desc}
                  </p>
                  <p className="text-2xl text-right">
                    <ConvertedAmount
                      amount={cost?.totalCostIncludingVAT || -1}
                      currency={cost?.currency || 'EUR'}
                      targetCurrency="EUR"
                    />
                  </p>
                </>
              ) : (
                <p className="text-2xl text-right">
                  {formatCurrency(cost?.totalCostIncludingVAT || -1, cost?.currency || 'EUR')}
                </p>
              )}
              <p className="text-md text-gray-500 text-right">
                {availableStock} stock
              </p>
              <p className="text-md font-medium text-blue-500 text-right">
                {supplierProductVariation.quantityOrdered} commandés
              </p>
            </>
          ) : (
            <p className="text-right text-md text-gray-500 mb-2">
              Not available
            </p>
          )}
        </div>
      </div>
    </div>
  );
}